import { useMemo } from 'react';
import { RotateCcw } from 'lucide-react';
import { SearchInput } from '@/components/ui/search-input';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { PicklistValue } from '@/api/client';
import type { TicketFilters } from '@/api/hooks';

interface Props {
  filters: TicketFilters;
  defaults: TicketFilters;
  onChange: (next: TicketFilters) => void;
  picklists?: { status: PicklistValue[]; priority: PicklistValue[]; queueID: PicklistValue[] };
  /** Show the "My tickets / All tickets" toggle. Hidden on unassigned-only views (Queues), where it would contradict the `unassigned` filter. */
  showAssignedToToggle?: boolean;
}

function MultiSelect({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: PicklistValue[];
  selected: number[];
  onChange: (next: number[]) => void;
}) {
  const summary = useMemo(() => {
    if (selected.length === 0) return label;
    if (selected.length === 1) {
      const v = options.find((o) => Number(o.value) === selected[0]);
      return `${label}: ${v?.label ?? selected[0]}`;
    }
    return `${label}: ${selected.length}`;
  }, [label, options, selected]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm">{summary}</Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-80 overflow-auto">
        <DropdownMenuLabel>{label}</DropdownMenuLabel>
        {options.map((o) => {
          const v = Number(o.value);
          const checked = selected.includes(v);
          return (
            <DropdownMenuCheckboxItem
              key={o.value}
              checked={checked}
              onCheckedChange={(c) => {
                onChange(c ? [...selected, v] : selected.filter((x) => x !== v));
              }}
              onSelect={(e) => e.preventDefault()}
            >
              {o.label}
            </DropdownMenuCheckboxItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function FilterBar({ filters, defaults, onChange, picklists, showAssignedToToggle = true }: Props) {
  const set = <K extends keyof TicketFilters>(k: K, v: TicketFilters[K]) => onChange({ ...filters, [k]: v });

  // Cheap-and-correct "is the user away from defaults?" check for small flat-ish objects.
  const isDefault = useMemo(() => JSON.stringify(filters) === JSON.stringify(defaults), [filters, defaults]);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <SearchInput
        withIcon
        placeholder="Search"
        className="w-64"
        value={filters.search}
        onValueChange={(v) => set('search', v)}
      />

      {showAssignedToToggle && (
        <Button
          variant={filters.assignedTo === 'me' ? 'default' : 'outline'}
          size="sm"
          onClick={() => set('assignedTo', filters.assignedTo === 'me' ? 'any' : 'me')}
        >
          {filters.assignedTo === 'me' ? 'My tickets' : 'All tickets'}
        </Button>
      )}

      {picklists && (
        <>
          <MultiSelect label="Status" options={picklists.status} selected={filters.status} onChange={(v) => set('status', v)} />
          <MultiSelect label="Priority" options={picklists.priority} selected={filters.priority} onChange={(v) => set('priority', v)} />
          <MultiSelect label="Queue" options={picklists.queueID} selected={filters.queueID} onChange={(v) => set('queueID', v)} />
        </>
      )}

      <Button
        variant="ghost"
        size="sm"
        onClick={() => onChange(defaults)}
        disabled={isDefault}
        title={isDefault ? 'Filters are at defaults' : 'Reset filters to this view\'s defaults'}
      >
        <RotateCcw className="mr-1 h-3 w-3" />Reset filters
      </Button>
    </div>
  );
}
